import type {
  ChatMessage,
  StakeArtifacts,
  StakeRoundExplain,
  StakeScenarioDetail,
  StakeScenarioMeta,
  StakeScenarioRowsPage,
  StakeScenarioShapeExplain,
} from '@sr-test-wizard/shared';
import { api } from './api';

/** Filter/sort/paging params for the scenario rows query. */
export interface ScenarioQuery {
  winType?: string[];
  features?: string[];
  multMin?: number | null;
  multMax?: number | null;
  /** Categorical attribute filters (attribute key -> required value). */
  attrs?: Record<string, string>;
  criteria?: string | null;
  eventType?: string | null;
  id?: number | null;
  explained?: 'yes' | 'no' | null;
  sort?: 'idAsc' | 'multDesc' | 'multAsc' | 'weightDesc' | 'oddsAsc';
  page?: number;
  pageSize?: number;
}

function scenarioQs(q: ScenarioQuery): string {
  const p = new URLSearchParams();
  if (q.winType?.length) p.set('winType', q.winType.join(','));
  if (q.features?.length) p.set('features', q.features.join(','));
  if (q.multMin != null) p.set('multMin', String(q.multMin));
  if (q.multMax != null) p.set('multMax', String(q.multMax));
  if (q.attrs) for (const [k, v] of Object.entries(q.attrs)) if (v) p.set(`attr_${k}`, v);
  if (q.criteria) p.set('criteria', q.criteria);
  if (q.eventType) p.set('eventType', q.eventType);
  if (q.id != null) p.set('id', String(q.id));
  if (q.explained) p.set('explained', q.explained);
  if (q.sort) p.set('sort', q.sort);
  if (q.page != null) p.set('page', String(q.page));
  if (q.pageSize != null) p.set('pageSize', String(q.pageSize));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * Client for the `/api/runs/:id/stake-engine` route module (Stake Engine
 * verification Extras feature, EAS only). Verification is fire-and-forget -
 * the page reflects progress from the `stake-engine-progress` WS stream and
 * re-fetches the report on completion. Chat is synchronous.
 */
export interface VerifyBody {
  doIntegrity?: boolean;
  doRoundTypes?: boolean;
  declaredRtp?: number | null;
  modes?: string[] | null;
}

export const stakeEngineApi = {
  get: (runId: string) => api.get<StakeArtifacts>(`/api/runs/${runId}/stake-engine`),
  verify: (runId: string, body: VerifyBody) =>
    api.post<{ started: boolean; alreadyRunning?: boolean }>(
      `/api/runs/${runId}/stake-engine/verify`,
      body,
    ),
  explain: (runId: string, mode: string) =>
    api.post<{ explain: StakeRoundExplain }>(`/api/runs/${runId}/stake-engine/explain`, { mode }),
  listChat: (runId: string) => api.get<ChatMessage[]>(`/api/runs/${runId}/stake-engine/chat`),
  sendChat: (runId: string, question: string) =>
    api.post<{ message: ChatMessage }>(`/api/runs/${runId}/stake-engine/chat`, { question }),
  resetChat: (runId: string) =>
    api.delete<{ cleared: boolean }>(`/api/runs/${runId}/stake-engine/chat`),

  scenariosBuild: (runId: string, mode: string) =>
    api.post<{ meta: StakeScenarioMeta }>(`/api/runs/${runId}/stake-engine/scenarios/build`, { mode }),
  scenariosMeta: (runId: string, mode: string) =>
    api.get<{ meta: StakeScenarioMeta | null }>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/meta`,
    ),
  scenariosRows: (runId: string, mode: string, q: ScenarioQuery) =>
    api.get<StakeScenarioRowsPage>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/rows${scenarioQs(q)}`,
    ),
  scenarioRecord: (runId: string, mode: string, id: number) =>
    api.get<{ detail: StakeScenarioDetail }>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/record/${id}`,
    ),
  scenariosExplainShapes: (runId: string, mode: string) =>
    api.post<{ shapes: StakeScenarioShapeExplain }>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/explain-shapes`,
      {},
    ),
  scenarioExplainOne: (runId: string, mode: string, id: number) =>
    api.post<{ explanation: string }>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/record/${id}/explain`,
      {},
    ),
  scenariosIds: (runId: string, mode: string, q: ScenarioQuery) =>
    api.get<{ ids: number[]; total: number }>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/ids${scenarioQs(q)}`,
    ),
  scenariosExplained: (runId: string, mode: string) =>
    api.get<{ ids: number[] }>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/explained`,
    ),
  scenariosExplainRounds: (runId: string, mode: string, ids: number[]) =>
    api.post<{ explained: number; fallback: number; total: number; stopped?: boolean }>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/explain-rounds`,
      { ids },
    ),
  scenariosExplainRoundsStop: (runId: string, mode: string) =>
    api.post<{ stopping: boolean }>(
      `/api/runs/${runId}/stake-engine/scenarios/${encodeURIComponent(mode)}/explain-rounds/stop`,
      {},
    ),
};
